import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TITLE_FONTS, TITLE_SIZES, extractPalette } from "@/lib/appearance";
import { toast } from "sonner";
import { Palette, Loader2 } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const ColorField = ({ label, value, onChange, testId, placeholder = "défaut" }) => (
  <div className="space-y-1.5">
    <Label className="text-overline !text-zinc-400">{label}</Label>
    <div className="flex gap-2 items-center">
      <Input
        type="color"
        className={`${inputClass} h-10 w-14 p-1 cursor-pointer`}
        value={value || "#D4AF37"}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-picker`}
      />
      <Input
        className={inputClass}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 hover:text-white shrink-0"
          data-testid={`${testId}-reset`}
        >
          Défaut
        </button>
      )}
    </div>
  </div>
);

/**
 * AppearancePanel — personnalisation complète des couleurs/titre d'un évènement.
 * appearance: {hide_title, title_color, title_size, title_font, heading_color, text_color, card_color}
 */
export default function AppearancePanel({
  appearance = {},
  onChange,
  imageUrl,
  accent,
  onAccent,
  testIdPrefix = "ap",
}) {
  const [palette, setPalette] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const set = (k, v) => onChange({ ...appearance, [k]: v });

  const extract = async () => {
    if (!imageUrl) {
      toast.error("Ajoute d'abord un logo ou une photo de couverture.");
      return;
    }
    setExtracting(true);
    try {
      const colors = await extractPalette(imageUrl);
      if (colors.length === 0) {
        toast.error("Aucune couleur exploitable détectée.");
        return;
      }
      setPalette(colors);
      onAccent?.(colors[0]);
      onChange({
        ...appearance,
        title_color: colors[0],
        heading_color: colors[1] || colors[0],
      });
      toast.success("Palette extraite ! Clique sur une pastille pour l'utiliser comme accent.");
    } catch {
      toast.error("Impossible de lire l'image (format ou CORS). Règle les couleurs manuellement.");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="bg-[#0A0B0E] border border-white/5 rounded-md p-4 space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={extract}
          disabled={extracting}
          data-testid={`${testIdPrefix}-extract`}
          className="bg-transparent border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#D4AF37]"
        >
          {extracting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Palette className="w-4 h-4 mr-2" />
          )}
          Extraire les couleurs du logo / de la photo
        </Button>
        {palette.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-palette`}>
            {palette.map((c) => (
              <button
                key={c}
                type="button"
                title={`${c} — utiliser comme couleur d'accent`}
                onClick={() => onAccent?.(c)}
                className="w-7 h-7 rounded-full border-2 border-white/20 hover:scale-110 transition-transform"
                style={{ background: c }}
                data-testid={`${testIdPrefix}-swatch-${c.replace("#", "")}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/5 pt-4 space-y-4">
        <div className="text-overline !text-zinc-400">Titre principal</div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid={`${testIdPrefix}-hide-title`}>
          <input
            type="checkbox"
            className="w-4 h-4 accent-[#D4AF37]"
            checked={!!appearance.hide_title}
            onChange={(e) => set("hide_title", e.target.checked)}
          />
          <span className="text-sm font-body text-zinc-300">Masquer le nom / gros titre sur la page publique</span>
        </label>
        {!appearance.hide_title && (
          <div className="grid sm:grid-cols-3 gap-4">
            <ColorField
              label="Couleur du titre"
              value={appearance.title_color}
              onChange={(v) => set("title_color", v)}
              testId={`${testIdPrefix}-title-color`}
              placeholder="dégradé or (défaut)"
            />
            <div className="space-y-1.5">
              <Label className="text-overline !text-zinc-400">Taille du titre</Label>
              <Select value={appearance.title_size || "l"} onValueChange={(v) => set("title_size", v)}>
                <SelectTrigger className={inputClass} data-testid={`${testIdPrefix}-title-size`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  {Object.entries(TITLE_SIZES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline !text-zinc-400">Police du titre</Label>
              <Select value={appearance.title_font || "sport"} onValueChange={(v) => set("title_font", v)}>
                <SelectTrigger className={inputClass} data-testid={`${testIdPrefix}-title-font`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  {Object.entries(TITLE_FONTS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      <span style={v.css ? { fontFamily: v.css } : {}}>{v.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="text-overline !text-zinc-400 mb-3">Couleurs de la page</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ColorField
            label="Couleur d'accent"
            value={accent}
            onChange={(v) => onAccent?.(v || "#D4AF37")}
            testId={`${testIdPrefix}-accent`}
            placeholder="#D4AF37"
          />
          <ColorField
            label="Titres de sections"
            value={appearance.heading_color}
            onChange={(v) => set("heading_color", v)}
            testId={`${testIdPrefix}-heading-color`}
            placeholder="blanc (défaut)"
          />
          <ColorField
            label="Textes / descriptions"
            value={appearance.text_color}
            onChange={(v) => set("text_color", v)}
            testId={`${testIdPrefix}-text-color`}
            placeholder="gris clair (défaut)"
          />
          <ColorField
            label="Cartes / blocs"
            value={appearance.card_color}
            onChange={(v) => set("card_color", v)}
            testId={`${testIdPrefix}-card-color`}
            placeholder="#111216 (défaut)"
          />
        </div>
      </div>
    </div>
  );
}
